import { Card as CardType, ListStyle } from '@yleisradio/areena-types';
import Chevron from 'assets/chevronRight.svg';
import classNames from 'classnames';
import { GradientOverlay } from 'components/Card/GradientOverlay';
import { usePlayerActionForPointer } from 'components/Controls/Control/actions/usePlayerAction';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { Link } from 'components/Link';
import { useRegionalChannelCookie } from 'hooks/useRegionalChannelCookie';
import dynamic from 'next/dynamic';
import React from 'react';
import { filterLabelsByType, findLabel } from 'utils/card';
import { isPlayerPointer } from 'utils/pointer';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import styles from './Card.module.scss';
import { CardImage } from './CardImage';
import { CardLabels } from './CardLabels';
import Description from './Description';
import { ImageOverlay } from './ImageOverlay';
import { OverlayLogoImage } from './OverlayLogoImage';
import { PlayButton } from './PlayButton';
import { RemoveButton } from './RemoveButton';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { GroupEngagementData } from 'components/SnowplowEngagementAnalytics/types';
import {
  useTrackCardClick,
  useViewUuid,
} from 'components/SnowplowEngagementAnalytics/hooks';
import { ViewableCard } from 'components/SnowplowEngagementAnalytics/ViewableCard';

const CardMenu = dynamic(() => import('./CardMenu'), { ssr: false });
const ProgressBar = dynamic(() => import('./ProgressBar'));

function isInVerticalList(listStyle: ListStyle | undefined): boolean {
  return !!listStyle && listStyle.layout === 'vertical';
}

interface CardProps {
  card: CardType;
  listStyle?: ListStyle | undefined;
  cardKey: string;
  groupEngagementData: GroupEngagementData;
  index: number;
  sizes: string;
  historyElapsed: string | null;
  isActive?: boolean;
  hideCard?: () => void;
  isDescriptionVisible?: boolean;
}

const getAspectRatioClassName = (listStyle: ListStyle | undefined) => {
  switch (listStyle?.image) {
    case '1:1':
      return styles.withAspectRatio1_1;
    case '16:9':
    default:
      return styles.withAspectRatio16_9;
  }
};

const useSelectedCard = (cardOrDeck: CardType): CardType => {
  const [selectedRegionalChannelId] = useRegionalChannelCookie(cardOrDeck);

  if (cardOrDeck.presentation === 'broadcastDeck' && cardOrDeck.cards) {
    const selectedRegionalChannelCard = cardOrDeck.cards.find(
      (card) => card.persistentState?.id === selectedRegionalChannelId
    );

    return selectedRegionalChannelCard || cardOrDeck.cards[0] || cardOrDeck;
  }

  return cardOrDeck;
};

export const Card: React.FunctionComponent<CardProps> = ({
  card: cardOrDeck,
  groupEngagementData,
  index,
  listStyle,
  cardKey,
  sizes,
  historyElapsed,
  isActive,
  hideCard,
  isDescriptionVisible = true,
}) => {
  const card = useSelectedCard(cardOrDeck);

  const { controls = [], labels = [], pointer } = card;

  const { runAction: startPlayer } =
    usePlayerActionForPointer({ pointer, cardPointer: pointer }) || {};
  const viewUuid = useViewUuid();
  const { trackCardClick, trackPlayButtonClick } = useTrackCardClick(viewUuid);
  const onClick = () => {
    if (isPlayerPointer(pointer)) {
      startPlayer?.();
      trackPlayButtonClick?.();
    } else {
      trackCardClick?.();
    }
  };

  const isVertical = isInVerticalList(listStyle);

  const accessibilityDescription = findLabel(
    labels,
    'accessibilityDescription'
  )?.formatted;
  const overlayLabel = findLabel(labels, 'overlay');
  const highlightLabels = filterLabelsByType('highlight', labels);
  const progressLabel = findLabel(labels, 'progress');
  const isBroadcastCard = card.presentation === 'broadcastCard';
  const genericLabels = filterLabelsByType('generic', labels);

  const removeControl = controls.find((c) => c.operation?.method === 'DELETE');

  const menuControls = controls.filter(
    (c) => !!c.tag && ['queue', 'downloadLink'].includes(c.tag)
  );

  const playControl = controls.find((c) => c.tag === 'play');

  const accessibilityDescriptionId =
    accessibilityDescription &&
    sanitizeHtmlId(cardKey + 'accessibilityDescription');
  const titleId = sanitizeHtmlId(cardKey + 'title');

  return (
    <ViewableCard
      card={card}
      cardIndex={index}
      viewUuid={viewUuid}
      groupData={groupEngagementData}
      viewMode="thumbnail"
    >
      <div
        className={classNames(
          styles.card,
          card.presentation === 'packageCard' && styles.packageCard,
          isVertical && styles.cardVertical,
          isActive && styles.cardActive,
          getAspectRatioClassName(listStyle)
        )}
        aria-labelledby={titleId}
        aria-describedby={accessibilityDescriptionId}
        role="group"
        data-testid="card"
      >
        {listStyle?.image !== 'none' ? (
          <div className={classNames(styles.imageContentWrapper)}>
            <CardImage
              image={card.image}
              imageStyle={listStyle?.image}
              sizes={sizes}
            />
            {card.overlayLogoImage && <GradientOverlay />}
            {card.overlayLogoImage && (
              <OverlayLogoImage
                logoImage={card.overlayLogoImage}
                listStyle={listStyle}
              />
            )}
            {overlayLabel && <ImageOverlay overlay={overlayLabel} />}
            {highlightLabels.length > 0 && !isVertical ? (
              <div className={styles.highlightWrapper}>
                <CardLabels isHighlight labels={highlightLabels} />
              </div>
            ) : null}
            {progressLabel ? (
              <div className={styles.progressBarWrapper}>
                <ErrorBoundary>
                  <ProgressBar
                    progressLabel={progressLabel}
                    historyElapsed={historyElapsed}
                  />
                </ErrorBoundary>
              </div>
            ) : null}
            {playControl ? (
              <div className={styles.buttonWrapper}>
                <PlayButton
                  control={playControl}
                  cardPointer={pointer}
                  isOverlay={isVertical}
                  trackClick={trackPlayButtonClick}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className={styles.textContentWrapper}>
          {highlightLabels.length > 0 && isVertical ? (
            <div className={styles.verticalHighlightWrapper}>
              <CardLabels isHighlight labels={highlightLabels} />
            </div>
          ) : null}
          {genericLabels.length > 0 && isBroadcastCard ? (
            <div className={styles.broadcastCardLabels}>
              <CardLabels labels={genericLabels} />
            </div>
          ) : null}
          <h3
            className={styles.title}
            dir="auto"
            id={titleId}
            data-testid="card-title"
          >
            {card.pointer ? (
              <Link
                className={styles.link}
                onClick={onClick}
                pointer={card.pointer}
              >
                {card.title}
              </Link>
            ) : (
              card.title
            )}
          </h3>
          <Description
            text={card.description}
            isVisible={isDescriptionVisible}
          />
          {genericLabels.length > 0 && !isBroadcastCard ? (
            <div className={styles.labels}>
              <CardLabels labels={genericLabels} />
            </div>
          ) : null}
        </div>

        {accessibilityDescription ? (
          <VisuallyHidden id={accessibilityDescriptionId}>
            {accessibilityDescription}
          </VisuallyHidden>
        ) : null}

        {isVertical ? (
          <>
            {menuControls.length > 0 ? (
              <div className={styles.controlWrapper}>
                <CardMenu controls={menuControls} />
              </div>
            ) : null}
            {removeControl ? (
              <div className={styles.controlWrapper}>
                <RemoveButton control={removeControl} hideCard={hideCard} />
              </div>
            ) : null}
            {menuControls.length === 0 && !removeControl ? (
              <div className={styles.arrowWrapper}>
                <Chevron aria-hidden={true} className={styles.arrowIcon} />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </ViewableCard>
  );
};
